import { jsPDF } from "jspdf";
import { displayElapsed } from "./finishTime";

/**
 * Top-10 podium recap, per gender, per category — an A4 PDF for the prize desk.
 *
 * Drawn as vector text rather than rasterised through html2canvas: the file
 * stays tens of kilobytes instead of megabytes, the names stay sharp at print
 * resolution, and it does not depend on a hidden DOM node being laid out and
 * visible at the moment of export.
 */

const MARGIN = 15;       // mm, comfortable on any office printer
const PAGE_W = 210;      // A4 portrait
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN * 2;

/** Column x-offsets within the content band. Name is widest; time is right-aligned. */
const COL = {
  rank: 0,
  bib: 16,
  name: 34,
  nat: 126,
  time: CONTENT_W, // right edge — drawn with { align: "right" }
};

/**
 * Times are stored to the millisecond because ranking needs it, but a podium
 * sheet read aloud at a prize ceremony does not. Matches the results pages.
 */
const stripMs = (v, category = null) =>
  typeof v === "string" ? displayElapsed(v, category) : v || "";

/** Long names must not run under the nationality column. */
const fit = (docRef, text, maxW) => {
  const s = String(text || "");
  if (docRef.getTextWidth(s) <= maxW) return s;
  let out = s;
  while (out.length > 1 && docRef.getTextWidth(out + "…") > maxW) {
    out = out.slice(0, -1);
  }
  return out + "…";
};

/**
 * A runner earns a podium slot only by finishing.
 *
 * The results API ranks everyone who started — a runner still on course holds
 * a provisional rank — so a rank alone does not mean a finish. Without this
 * the 42K sheet would have printed ten "winners" mid-race.
 *
 * `finishDuration` cannot be the test on its own. A runner whose chip was
 * missed at the start mat has no start instant to subtract, so the backend
 * leaves the duration null while still holding a valid gun time: SMM 2026 BIB
 * 2132 finished 3rd fastest of the 21K men and was dropped from the recap
 * entirely. Either field proves the runner crossed the line.
 */
const isFinisher = (r) => Boolean(r?.finishDuration || r?.finishTime);

/**
 * What to print in the TIME column.
 *
 * `finishDuration` is the net elapsed time and is preferred. When the start
 * was missed it does not exist, and `finishTime` — the gun time — is the only
 * honest figure available.
 */
const displayTime = (r, category = null) => stripMs(r?.finishDuration || r?.finishTime, category);

/**
 * Pick the top N finishers of one gender, ordered by the placing the backend
 * already computed. `genderRank` is authoritative: it encodes the COT and
 * tie-break rules, so re-sorting on raw time here could disagree with the
 * public results page.
 */
const topOfGender = (rows, gender, limit) =>
  rows
    .filter((r) => r.gender === gender && isFinisher(r) && r.genderRank > 0)
    .sort((a, b) => a.genderRank - b.genderRank)
    .slice(0, limit);

/**
 * Mixed teams (double mixed, relay mixed) carry gender "mixed" and no gender
 * rank, so topOfGender drops them and a mixed-only category printed no page at
 * all. They are listed by overall place instead.
 */
const topMixed = (rows, limit) =>
  rows
    .filter((r) => r.gender !== "male" && r.gender !== "female" && isFinisher(r) && r.overallRank > 0)
    .sort((a, b) => a.overallRank - b.overallRank)
    .slice(0, limit);

/** Draws one gender block; returns the y cursor after it. */
const drawGenderTable = (docRef, title, rows, startY, category = null) => {
  let y = startY;

  // Set in type, not in a coloured bar. An official result sheet is read at a
  // prize desk and photocopied; letter-spaced caps over a rule carry the
  // hierarchy without ink, and survive a bad photocopier better than a tint.
  docRef.setTextColor(0, 0, 0);
  docRef.setFont("helvetica", "bold");
  docRef.setFontSize(9.5);
  docRef.text(title, MARGIN, y + 3.5, { charSpace: 0.6 });
  y += 5.5;
  docRef.setDrawColor(0, 0, 0);
  docRef.setLineWidth(0.4);
  docRef.line(MARGIN, y, MARGIN + CONTENT_W, y);

  // Column headers
  docRef.setTextColor(0, 0, 0);
  docRef.setFont("helvetica", "normal");
  docRef.setFontSize(7);
  y += 4.5;
  docRef.text("#", MARGIN + COL.rank, y);
  docRef.text("BIB", MARGIN + COL.bib, y);
  docRef.text("NAME", MARGIN + COL.name, y);
  docRef.text("NAT", MARGIN + COL.nat, y);
  docRef.text("TIME", MARGIN + COL.time, y, { align: "right" });
  y += 1.8;
  docRef.setDrawColor(0, 0, 0);
  docRef.setLineWidth(0.2);
  docRef.line(MARGIN, y, MARGIN + CONTENT_W, y);
  y += 4.8;

  if (rows.length === 0) {
    docRef.setFont("helvetica", "normal");
    docRef.setFontSize(8);
    docRef.setTextColor(120, 120, 120);
    docRef.text("No finishers yet.", MARGIN, y);
    return y + 7;
  }

  rows.forEach((r, i) => {
    // No zebra tint: at ten rows the eye tracks a line fine, and the banding
    // is the first thing to turn muddy on a thermal or toner-low printer.
    //
    // The first three places carry the medals, so they are set in bold. That
    // is the only emphasis on the sheet, which is what makes it read.
    const podium = i < 3;
    docRef.setFont("helvetica", podium ? "bold" : "normal");
    docRef.setFontSize(9);
    docRef.setTextColor(0, 0, 0);

    docRef.text(String(i + 1), MARGIN + COL.rank, y);
    docRef.text(String(r.bib ?? ""), MARGIN + COL.bib, y);
    docRef.text(
      fit(docRef, r.name, COL.nat - COL.name - 4),
      MARGIN + COL.name,
      y,
    );

    docRef.setFont("helvetica", "normal");
    docRef.setFontSize(7.5);
    docRef.setTextColor(90, 90, 90);
    docRef.text(
      fit(docRef, r.nationality || "", 24),
      MARGIN + COL.nat,
      y,
    );

    // Monospaced digits so the times form a readable column.
    docRef.setFont("courier", podium ? "bold" : "normal");
    docRef.setFontSize(9);
    docRef.setTextColor(0, 0, 0);
    docRef.text(displayTime(r, category), MARGIN + COL.time, y, {
      align: "right",
    });

    y += 5.6;
  });

  return y + 4;
};

/**
 * Build and download the recap.
 *
 * @param {Array}  results    Rows from /api/timing/results (all pages).
 * @param {Array}  categories Event category definitions, for display names and order.
 * @param {string} eventName  Printed as the document header.
 * @param {string} eventId    Used for the filename.
 * @param {number} limit      Places per gender. Default 10.
 */
export const generateAndDownloadPodiumPdf = (
  results,
  categories,
  eventName,
  eventId,
  limit = 10,
) => {
  if (!results || results.length === 0) {
    throw new Error("No results to export");
  }

  const finishers = results.filter(isFinisher);
  if (finishers.length === 0) {
    throw new Error("No finishers yet — nothing to print");
  }

  const docRef = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

  // Follow the order the organiser set in admin, not the order rows happened to
  // arrive in, then append any category present in results but not in config so
  // a mis-tagged runner is never silently dropped from the recap.
  const configured = (categories || []).map((c) => c.id);
  const seen = [...new Set(results.map((r) => r.categoryId).filter(Boolean))];
  const order = [
    ...configured.filter((id) => seen.includes(id)),
    ...seen.filter((id) => !configured.includes(id)),
  ];

  const nameOf = (id) =>
    (categories || []).find((c) => c.id === id)?.name || id;

  const printedAt = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  let first = true;

  order.forEach((catId) => {
    const rows = results.filter((r) => r.categoryId === catId);
    const catDef = (categories || []).find((c) => c.id === catId) || null;
    const male = topOfGender(rows, "male", limit);
    const female = topOfGender(rows, "female", limit);
    const mixed = topMixed(rows, limit);

    // A category with nobody across the line yet contributes no page at all,
    // rather than a page of empty tables.
    if (male.length === 0 && female.length === 0 && mixed.length === 0) return;

    if (!first) docRef.addPage();
    first = false;

    let y = MARGIN;

    // ── Header ──────────────────────────────────────────────────────────────
    docRef.setFont("helvetica", "bold");
    docRef.setFontSize(14);
    docRef.setTextColor(0, 0, 0);
    docRef.text(String(eventName || "RACE RESULTS").toUpperCase(), MARGIN, y + 2);
    y += 7;

    docRef.setFont("helvetica", "normal");
    docRef.setFontSize(10.5);
    docRef.setTextColor(0, 0, 0);
    docRef.text(`${nameOf(catId)} — TOP ${limit}`, MARGIN, y);
    y += 4.5;

    docRef.setFontSize(7.5);
    docRef.setTextColor(120, 120, 120);
    docRef.text(`Printed ${printedAt}`, MARGIN, y);
    y += 2.5;

    docRef.setDrawColor(0, 0, 0);
    docRef.setLineWidth(0.6);
    docRef.line(MARGIN, y, MARGIN + CONTENT_W, y);
    y += 8;

    // ── Tables ──────────────────────────────────────────────────────────────
    // One block per gender that finished: a women-only category gets no empty
    // MALE table (as on the results pages), and mixed teams get their own.
    const blocks = [
      ["MALE", male],
      ["FEMALE", female],
      ["MIXED", mixed],
    ].filter(([, list]) => list.length > 0);
    blocks.forEach(([title, list], index) => {
      if (index > 0) {
        y += 4;
        // Keep a block whole: if it cannot fit below the previous one, it
        // starts a fresh page rather than splitting across the fold.
        const needed = 12 + Math.max(list.length, 1) * 5.6 + 10;
        if (y + needed > PAGE_H - MARGIN) {
          docRef.addPage();
          y = MARGIN;
          docRef.setFont("helvetica", "normal");
          docRef.setFontSize(10.5);
          docRef.setTextColor(0, 0, 0);
          docRef.text(`${nameOf(catId)} — TOP ${limit} (cont.)`, MARGIN, y);
          y += 8;
        }
      }
      y = drawGenderTable(docRef, title, list, y, catDef);
    });

    // ── Footer ──────────────────────────────────────────────────────────────
    // Stated on every page: these are printed while the protest window may
    // still be open, and a sheet handed to a prize desk must say so itself.
    docRef.setFont("helvetica", "normal");
    docRef.setFontSize(7);
    docRef.setTextColor(120, 120, 120);
    docRef.text(
      "Unofficial — official results follow the protest period.",
      MARGIN,
      PAGE_H - 8,
    );
    docRef.text(
      String(nameOf(catId)),
      MARGIN + CONTENT_W,
      PAGE_H - 8,
      { align: "right" },
    );
  });

  if (first) {
    throw new Error("No finishers yet — nothing to print");
  }

  const stamp = new Date().toISOString().slice(0, 10);
  docRef.save(`${eventId || "event"}_top${limit}_${stamp}.pdf`);
};

export default generateAndDownloadPodiumPdf;
